import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, Injector, NgModule, ErrorHandler } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy, Router } from '@angular/router';
import { createCustomElement } from '@angular/elements';
import { tap } from 'rxjs';
import { TraceService, createErrorHandler } from '@sentry/angular';
import { provideUniPinGql } from '@unipin/angular-gql';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { initializeApp } from 'firebase/app';
import { environment } from '@env/environment';
import { AuthResolver } from '@core/resolvers/auth.resolver';
import { headerInterceptor } from '@core/interceptors/header.interceptor';
import { refreshTokenInterceptor } from './core/interceptors/refresh-token.interceptor';
import { AuthService } from '@core/services/auth.service';
import { ProfileService } from '@core/services/profile.service';
import { CountryService } from './core/services/country.service';
import { AppComponent } from '@app/app.component';
import { ShellComponent } from '@core/components/shell/shell.component';
import { AdsenseComponent } from '@core/components/adsense/adsense.component';
import { AppRoutingModule } from '@app/app-routing.module';
import { TranslocoRootModule } from './transloco-root.module';

initializeApp(environment.firebaseConfig);

@NgModule({
  bootstrap: [
    AppComponent
  ],
  declarations: [
    AppComponent
  ],
  schemas: [
    CUSTOM_ELEMENTS_SCHEMA
  ],
  imports: [
    BrowserModule, 
    AppRoutingModule,
    TranslocoRootModule,
    IonicModule.forRoot({ 
      mode: 'md',
      loadingSpinner: 'dots',
    }),

    ShellComponent,
    // ServiceWorkerModule.register(
    //   'ngsw-worker.js',
    //   {
    //     enabled: !isDevMode(),
    //     registrationStrategy: 'registerWhenStable:3000'
    //   }
    // )
  ],
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    
    provideUniPinGql(
      {
        uri: environment.gqlUrl,
        useOperationPath: true,
      }
    ),
    provideHttpClient(
      withInterceptors([ 
        headerInterceptor,
        refreshTokenInterceptor
      ])
    ),

    {
      provide: ErrorHandler,
      useValue: createErrorHandler(),
    },
    {
      provide: TraceService,
      deps: [Router],
    },
    
    {
      multi: true,
      provide: APP_INITIALIZER,
      deps: [ 
        AuthResolver,
        CountryService,
        ProfileService,
        AuthService,
        TraceService
      ],
      useFactory: (r: AuthResolver, c: CountryService, p: ProfileService, a: AuthService) => {
        if (environment.isMaintenance) {
          const r = document.getElementsByTagName('ups-root')[0] as HTMLElement;
          
          r.insertAdjacentHTML(
            'beforeend',
            `<img 
              class="h-100 w-100"
              alt="Maintenance Image"
              style="object-fit: cover;"
              src="/assets/images/maintenance.webp"
            />
          `);
          return;
        }
        
        /**
         * To be removed after v2 migration done. Expected to be removed at the end of the year.
         */
        const v2 = localStorage?.getItem('v2');
        if (!v2 && localStorage) {
          localStorage.clear();
          localStorage.setItem('v2', 'true');
        }

        c.init();
        AuthService.authToken = localStorage?.getItem('authToken') || '';

        if (AuthService.authToken === '' || r.isTokenExpired(AuthService.authToken)) {
          a.logout();
          return () => true;
        }

        return () => p.query().pipe(
          tap({
            next: ({ authMsMyProfile }) => {
              if (!authMsMyProfile) a.logout();
              a.setUser({ user: authMsMyProfile! as any, accessToken: AuthService.authToken });
            }
          })
        );
      }
    }
  ],
})
export class AppModule {

  constructor(
    protected readonly injector: Injector
  ) {
    customElements.define('ups-adsense', createCustomElement(AdsenseComponent, { injector }));
  }
}
